import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { INFERRED_TYPE_ENUM } from 'constants/chart-channels';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
} from 'utils/chart/transforms';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { getScaleMinMaxProps } from 'utils/chart/scale-min-max';

export default {
  name: 'Line Continuous',
  displayName: 'Continuous',
  order: 1,
  flavour: 'continuous',
  category: CHART_CATEGORIES.LINE,
  iconValue: 'line-continuous',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: true,
      channelType: 'value',
      intent: 'x-value',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'value',
      intent: 'y-value',
      multipleSeries: true,
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  // eslint-disable-next-line complexity
  specFn: (encodings, customisations) => {
    const isLogScaleEnabled = c =>
      get(customisations.axes, `${c}.logScale.enabled`, false);
    const isDate = c => {
      const { inferredType, transformedType } = get(encodings, c, {});
      return (transformedType || inferredType) === INFERRED_TYPE_ENUM.DATE;
    };
    // eslint-disable-next-line no-nested-ternary
    const sortField = isDate('x') ? 'x' : isDate('y') ? 'y' : 'x';
    const sortByY = sortField === 'y';
    const scaleType = c => {
      if (isDate(c)) {
        return 'utc';
      }
      return isLogScaleEnabled(c) ? 'log' : 'linear';
    };
    const sortParams = generateVegaSortParams(encodings);
    // do not pass logScale because we always want Vega to
    // decide the min value even if log scale has been enabled
    const xAxisProps = getScaleMinMaxProps(customisations, 'x');
    const yAxisProps = getScaleMinMaxProps(customisations, 'y');
    const showDataMarkers = get(customisations.options, 'dataMarkers.enabled');
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('x', encodings),
            ...generateVegaTypeCoercionTransforms('y', encodings),
          ],
        },
      ],
      marks: [
        {
          name: 'pathgroup',
          type: 'group',
          from: {
            facet: {
              name: 'faceted_path_main',
              data: 'transformedData',
              groupby: ['color'],
            },
          },
          encode: {
            update: {
              width: { field: { group: 'width' } },
              height: { field: { group: 'height' } },
            },
          },
          marks: [
            {
              name: 'marks',
              type: 'line',
              style: ['line'],
              sort: { field: `datum.${sortField}`, order: 'ascending' },
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  x: { scale: 'x', field: 'x' },
                  y: { scale: 'y', field: 'y' },
                  stroke: { scale: 'color', field: 'color' },
                },
              },
            },
            {
              type: 'symbol',
              from: { data: 'faceted_path_main' },
              encode: {
                update: {
                  x: { scale: 'x', field: 'x' },
                  y: { scale: 'y', field: 'y' },
                  fill: showDataMarkers
                    ? { scale: 'color', field: 'color' }
                    : { value: 'transparent' },
                  stroke: {
                    scale: 'color',
                    field: 'color',
                  },
                  strokeWidth: { value: showDataMarkers ? 1.5 : 0 },
                  size: { value: 30 },
                  tooltip: {
                    signal: generateVegaTooltip(encodings, customisations),
                  },
                },
              },
            },
            ...(!get(customisations.options, 'dataValueLabels.enabled')
              ? []
              : [
                  {
                    type: 'text',
                    name: 'valueLabels',
                    from: { data: 'faceted_path_main' },
                    encode: {
                      enter: {
                        text: {
                          signal: generateVegaLabelTextExpression(
                            encodings[sortByY ? 'x' : 'y'],
                            customisations.channels[sortByY ? 'x' : 'y'],
                            { field: sortByY ? 'x' : 'y' }
                          ),
                        },
                        dy: { value: showDataMarkers ? -6 : -3 },
                        x: { scale: 'x', field: 'x' },
                        y: { scale: 'y', field: 'y' },
                        align: { value: 'center' },
                      },
                    },
                  },
                ]),
          ],
        },
      ],
      scales: [
        {
          name: 'x',
          type: scaleType('x'),
          domain: { data: 'transformedData', field: 'x' },
          range: [0, { signal: 'width' }],
          zero: false,
          ...xAxisProps,
        },
        {
          name: 'y',
          type: scaleType('y'),
          domain: { data: 'transformedData', field: 'y' },
          range: [{ signal: 'height' }, 0],
          ...yAxisProps,
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: true,
          title: get(encodings, 'x.axis.title', ''),
          tickCount: { signal: 'ceil(width/60)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x,
                    { withDecimals: false, withSpacePadding: true }
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          title: get(encodings, 'y.axis.title', ''),
          tickCount: { signal: 'ceil(height/40)' },
          encode: {
            labels: {
              update: {
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.y,
                    customisations.channels.y,
                    { withDecimals: false }
                  ),
                },
              },
            },
          },
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              fill: 'color',
              title: get(encodings, 'color.legend.title'),
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
    };
  },
};
